import { emptyStateFor, type EmptyStateKind } from '../../lib/emptyState'
import type { WayInPage } from '../../lib/api'

/**
 * Which of the six things the Enrollment list is currently saying.
 *
 * A thin wrapper over `emptyStateFor`, and it exists for one decision that wrapper cannot make on its
 * own: **this list opens on a filter.** `live` is the default, because the question somebody arrives
 * with is "what can get into my organization right now", and on a seeded stack that is three rows out
 * of fourteen hundred.
 *
 * That default collides with the first-run state. `emptyStateFor` tests `filtered` before it tests
 * whether anything has ever existed, so a brand-new organization - nothing built, nothing issued -
 * would open on Live, find nothing, and be told "Nothing here under this filter. Other kits and tokens
 * are here under the other filters." There are no other kits and tokens. It would be pointing at rows
 * that do not exist, on the one screen where a new customer most needs to be told what to do first
 * (D-446).
 *
 * So a filter only counts as narrowing when there is something for it to narrow. `counts.all` is the
 * fleet-wide total the server sends with the page, which is exactly the fact needed and is already on
 * the wire - deriving it from `items` would be the page-counted-as-the-fleet defect again.
 */
export interface WaysInStateSource {
  page: WayInPage | null
  error: string | null
  loading: boolean
  /** The selected segment. `all` means no state filter. */
  state: string
  search: string
}

export function waysInState(source: WaysInStateSource): EmptyStateKind {
  const { page, error, loading, state, search } = source

  // Null when the read did not arrive, which is an error rather than an empty organization. Never a
  // zero: a value that was not measured is never reported as a value that was (D-263).
  const everHad = page === null ? null : (page.counts.all ?? 0) > 0
  const narrowed = state !== 'all' || search.trim() !== ''

  return emptyStateFor({
    value: page?.items ?? null,
    error,
    loading,
    // A filter narrows nothing when there is nothing to narrow.
    filtered: narrowed && everHad === true,
    everHadRows: everHad ?? undefined
  })
}
